import { useState, useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';

/**
 * Unified network status hook that uses @capacitor/network on native platforms
 * and falls back to browser navigator.onLine + events on web.
 */
export const useNativeNetwork = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [connectionType, setConnectionType] = useState('unknown');

  const updateFromNative = useCallback(async () => {
    if (!Capacitor.isNativePlatform()) return;
    try {
      const { Network } = await import('@capacitor/network');
      const status = await Network.getStatus();
      setIsOnline(status.connected);
      setConnectionType(status.connectionType || 'unknown');
    } catch (e) {
      console.warn('[Network] Failed to get native status:', e);
    }
  }, []);

  // Browser event listeners setup
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Native network listener setup
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let removeListener;

    const setup = async () => {
      try {
        const { Network } = await import('@capacitor/network');

        // Get initial status
        const status = await Network.getStatus();
        setIsOnline(status.connected);
        setConnectionType(status.connectionType || 'unknown');

        // Listen for changes
        removeListener = await Network.addListener('networkStatusChange', (s) => {
          setIsOnline(s.connected);
          setConnectionType(s.connectionType || 'unknown');
        });
      } catch (e) {
        console.warn('[Network] Capacitor Network plugin not available:', e);
      }
    };

    setup();

    return () => {
      if (removeListener && typeof removeListener.remove === 'function') {
        removeListener.remove();
      }
    };
  }, []);

  return {
    isOnline,
    connectionType,
    refresh: updateFromNative,
  };
};
